import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./WhatsNew.css";
import AddUpdatesModal from "./AddUpdatesModal";
import OrgPicker from "./OrgPicker";
import {
    whatsNewApi, CATEGORY_VALUES, PUSH_CHIP, STATUS_CHIP, fmtDate,
} from "./whatsNewApi";

/**
 * One release: its updates, who each goes to, and publishing.
 *
 * The screen is built around the blocker list the backend returns on every
 * read. Publishing is disabled while anything is outstanding and the reasons
 * are shown up front, so the team learns what is missing before pressing a
 * button that reaches hundreds of hospitals — not from a failed attempt.
 */
export default function WhatsNewReleaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [release, setRelease] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [results, setResults] = useState(null);
    const [confirm, setConfirm] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (msg, kind = "ok") => {
        setToast({ msg, kind });
        setTimeout(() => setToast(null), 3600);
    };

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const res = await whatsNewApi.getRelease(id);
            setRelease(res.data);
        } catch (e) {
            setErr(e.message || "Could not load this release.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const blockers = release?.blockers || [];
    const canPublish = blockers.length === 0;
    const isPublished = release?.status === "published";

    const doPublish = async () => {
        setConfirm(false);
        setPublishing(true);
        try {
            const res = await whatsNewApi.publishRelease(id);
            setResults(res.push_results || res.data?.push_results || []);
            await load();
            if (res.status === "success") showToast("Published to all deployments.");
            else showToast(res.message || "Some deployments failed.", "err");
        } catch (e) {
            const payload = e.payload || {};
            if (payload.blockers?.length) {
                setRelease((r) => (r ? { ...r, blockers: payload.blockers } : r));
            }
            showToast(e.message || "Publish failed.", "err");
        } finally {
            setPublishing(false);
        }
    };

    const doRetract = async () => {
        if (!window.confirm(
            "Retract this release? Customers stop seeing it immediately. " +
            "Read history is kept."
        )) return;
        try {
            await whatsNewApi.unpublishRelease(id);
            await load();
            showToast("Retracted.");
        } catch (e) {
            showToast(e.message || "Could not retract.", "err");
        }
    };

    if (loading) return <div className="wn-page">Loading…</div>;
    if (err) return <div className="wn-page"><div className="wn-empty">{err}</div></div>;
    if (!release) return null;

    const targetCount = countTargets(release);

    return (
        <div className="wn-page">
            <div className="wn-head">
                <button className="wn-btn wn-btn--sm" onClick={() => navigate("/whats-new")}>
                    ←
                </button>
                <h1>{release.title || `Release ${release.id}`}</h1>
                <span className={(STATUS_CHIP[release.status] || STATUS_CHIP.draft).cls}>
                    {(STATUS_CHIP[release.status] || STATUS_CHIP.draft).label}
                </span>
                <span className="wn-sp" />
                <span className="wn-hint">Released {fmtDate(release.release_date)}</span>
                <button className="wn-btn wn-btn--sm" onClick={() => setEditing(true)}>
                    Edit details
                </button>
                {isPublished && (
                    <button className="wn-btn wn-btn--danger wn-btn--sm" onClick={doRetract}>
                        Retract
                    </button>
                )}
                <button className="wn-btn" onClick={() => setAdding(true)}>
                    Add updates
                </button>
                <button
                    className="wn-btn wn-btn--primary"
                    disabled={!canPublish || publishing}
                    onClick={() => setConfirm(true)}
                    title={canPublish ? "" : "Resolve the blockers listed above first"}
                >
                    {publishing ? "Sending…" : isPublished ? "Re-send" : "Send to clients"}
                </button>
            </div>

            {/* Deployment ledger — per server, because one can fail alone. */}
            <div className="wn-orgs" style={{ marginBottom: 14 }}>
                {(release.servers || []).map((s) => {
                    const chip = PUSH_CHIP[s.push_status] || PUSH_CHIP.pending;
                    return (
                        <span key={s.id} className={chip.cls} title={s.push_message}>
                            {s.server_label || s.server_code}
                            {s.version_label ? ` · ${s.version_label}` : ""} — {chip.label}
                        </span>
                    );
                })}
            </div>

            {blockers.length > 0 ? (
                <div className="wn-blockers">
                    <h4>Not ready to send</h4>
                    <ul>
                        {blockers.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                </div>
            ) : (
                <div className="wn-ok-banner">
                    Ready to send — {release.items?.length || 0} update
                    {release.items?.length === 1 ? "" : "s"} to {targetCount}.
                </div>
            )}

            {(release.items || []).length === 0 && (
                <div className="wn-panel">
                    <div className="wn-empty">
                        <p>No updates in this release yet.</p>
                        <button className="wn-btn wn-btn--primary" onClick={() => setAdding(true)}>
                            Add updates from a sprint
                        </button>
                    </div>
                </div>
            )}

            {(release.items || []).map((item) => (
                <ItemEditor
                    key={item.id}
                    item={item}
                    onSaved={load}
                    onToast={showToast}
                />
            ))}

            {editing && (
                <EditReleaseModal
                    release={release}
                    onClose={() => setEditing(false)}
                    onSaved={() => {
                        setEditing(false);
                        load();
                        showToast("Release details saved.");
                    }}
                    onError={(m) => showToast(m, "err")}
                />
            )}

            {adding && (
                <AddUpdatesModal
                    releaseId={id}
                    onClose={() => setAdding(false)}
                    onAdded={(added, msg) => {
                        setAdding(false);
                        load();
                        showToast(msg || `${added.length} update(s) added.`);
                    }}
                    onError={(m) => showToast(m, "err")}
                />
            )}

            {confirm && (
                <ConfirmPublish
                    release={release}
                    targetCount={targetCount}
                    onCancel={() => setConfirm(false)}
                    onConfirm={doPublish}
                />
            )}

            {results && (
                <PublishResults results={results} onClose={() => setResults(null)} />
            )}

            {toast && <div className={`wn-toast wn-toast--${toast.kind}`}>{toast.msg}</div>}
        </div>
    );
}

/** Human summary of the blast radius: "all organizations" beats a number. */
function countTargets(release) {
    const items = release.items || [];
    if (items.some((i) => i.audience === "all")) return "all organizations";
    const keys = new Set();
    items.forEach((i) =>
        (i.orgs || []).forEach((o) => keys.add(`${o.server_code}:${o.org_id}`))
    );
    return `${keys.size} organization${keys.size === 1 ? "" : "s"}`;
}

/* ── edit release details ────────────────────────────────────────────────── */

/**
 * Title, release date and the per-deployment version labels.
 *
 * Version lives on the deployment row rather than the release, because the same
 * set of changes ships under a different build number on each eMedHub
 * instance. Clearing a version removes that deployment from the send — which is
 * how you retarget a release without deleting and rebuilding it.
 */
function EditReleaseModal({ release, onClose, onSaved, onError }) {
    const [title, setTitle] = useState(release.title || "");
    const [releaseDate, setReleaseDate] = useState(release.release_date || "");
    const [versions, setVersions] = useState(() =>
        Object.fromEntries(
            (release.servers || []).map((s) => [s.server_code, s.version_label || ""])
        )
    );
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!title.trim()) return;
        setSaving(true);
        try {
            await whatsNewApi.updateRelease(release.id, {
                title: title.trim(),
                release_date: releaseDate || null,
                servers: (release.servers || []).map((s) => ({
                    server_code: s.server_code,
                    version_label: (versions[s.server_code] || "").trim(),
                })),
            });
            onSaved();
        } catch (e) {
            onError(e.message || "Could not save the release.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="wn-ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="wn-modal" role="dialog" aria-label="Edit release">
                <div className="wn-m-head">
                    <h3>Edit release details</h3>
                    <button className="wn-x" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="wn-m-body">
                    <label className="wn-label" htmlFor="wn-edit-title">Title</label>
                    <input
                        id="wn-edit-title"
                        className="wn-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />

                    <label className="wn-label" htmlFor="wn-edit-date">Released on</label>
                    <input
                        id="wn-edit-date"
                        className="wn-input"
                        type="date"
                        value={releaseDate || ""}
                        onChange={(e) => setReleaseDate(e.target.value)}
                    />

                    <label className="wn-label">Version number per deployment</label>
                    {(release.servers || []).length === 0 && (
                        <div className="wn-hint wn-hint--warn">
                            This release has no deployments attached.
                        </div>
                    )}
                    {(release.servers || []).map((s) => (
                        <div key={s.id} className="wn-row" style={{ marginBottom: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", fontSize: 13 }}>
                                {s.server_label || s.server_code}
                            </div>
                            <input
                                className="wn-input"
                                placeholder="v-4.18.5"
                                value={versions[s.server_code] || ""}
                                onChange={(e) =>
                                    setVersions((v) => ({ ...v, [s.server_code]: e.target.value }))
                                }
                            />
                        </div>
                    ))}
                    <div className="wn-hint">
                        This is the release number your customers see.
                    </div>

                    {release.status === "published" && (
                        <div className="wn-hint wn-hint--warn" style={{ marginTop: 10 }}>
                            This release is already published. Saving changes it here only —
                            press Re-send afterwards to push the new details to customers.
                        </div>
                    )}
                </div>
                <div className="wn-m-foot">
                    <button className="wn-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="wn-btn wn-btn--primary"
                        onClick={submit}
                        disabled={saving || !title.trim()}
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── one update ──────────────────────────────────────────────────────────── */

function ItemEditor({ item, onSaved, onToast }) {
    const [clientText, setClientText] = useState(item.client_text || "");
    const [steps, setSteps] = useState(item.steps || "");
    const [category, setCategory] = useState(item.category || "feature");
    const [audience, setAudience] = useState(item.audience || "selected");
    const [orgs, setOrgs] = useState(item.orgs || []);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(item.needs_rewrite);

    const dirty =
        clientText !== (item.client_text || "") ||
        steps !== (item.steps || "") ||
        category !== (item.category || "feature") ||
        audience !== (item.audience || "selected") ||
        JSON.stringify(orgs.map(keyOf).sort()) !==
            JSON.stringify((item.orgs || []).map(keyOf).sort());

    // Mirrors the server's rule exactly: blank, or still identical to the
    // internal description, means nobody has rewritten it.
    const stillInternal =
        !clientText.trim() ||
        clientText.trim().toLowerCase() ===
            (item.source_desc || "").trim().toLowerCase();

    const save = async () => {
        setSaving(true);
        try {
            await whatsNewApi.updateItem(item.id, {
                client_text: clientText,
                steps,
                category,
                audience,
                orgs: audience === "selected"
                    ? orgs.map((o) => ({ server_code: o.server_code, org_id: o.org_id }))
                    : [],
            });
            onToast("Saved.");
            onSaved();
        } catch (e) {
            onToast(e.message || "Could not save.", "err");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Remove ${item.ticket_ref} from this release?`)) return;
        try {
            await whatsNewApi.deleteItem(item.id);
            onToast("Removed.");
            onSaved();
        } catch (e) {
            onToast(e.message || "Could not remove.", "err");
        }
    };

    return (
        <div className={`wn-item ${item.needs_rewrite ? "wn-item--needs" : ""}`}>
            <div className="wn-item-top">
                <span className="wn-item-ref">{item.ticket_ref}</span>
                <span style={{ fontSize: 13.5 }}>
                    {item.client_text || <em style={{ color: "var(--wn-red)" }}>Needs a customer-facing sentence</em>}
                </span>
                <span className="wn-item-sp" />
                {item.audience === "all" ? (
                    <span className="wn-org wn-org--all">All organizations</span>
                ) : (
                    <span className="wn-org">
                        {(item.orgs || []).length} org{(item.orgs || []).length === 1 ? "" : "s"}
                    </span>
                )}
                <button className="wn-btn wn-btn--sm" onClick={() => setOpen((o) => !o)}>
                    {open ? "Collapse" : "Edit"}
                </button>
            </div>

            {open && (
                <>
                    {/* Shown for reference only. This text is never published — it
                        names the organization that asked for the change. */}
                    {item.source_desc && (
                        <div className="wn-src">
                            <b>Internal ticket text — never sent to customers</b>
                            {item.source_desc}
                        </div>
                    )}

                    <label className="wn-label" htmlFor={`ct-${item.id}`}>
                        What the customer reads
                    </label>
                    <input
                        id={`ct-${item.id}`}
                        className="wn-input"
                        maxLength={500}
                        placeholder="Pharmacy: bill cancellation can now be restricted per user."
                        value={clientText}
                        onChange={(e) => setClientText(e.target.value)}
                    />
                    {stillInternal ? (
                        <div className="wn-hint wn-hint--warn">
                            Rewrite this in your own words. Copying the internal text sends
                            the requesting hospital&apos;s name to every other customer on
                            this update.
                        </div>
                    ) : (
                        <div className="wn-hint">{clientText.length}/500</div>
                    )}

                    <label className="wn-label" htmlFor={`st-${item.id}`}>
                        How to use — one step per line
                    </label>
                    <textarea
                        id={`st-${item.id}`}
                        className="wn-textarea"
                        placeholder={"Open Pharmacy\nGo to Stock List\nClick Print Barcode"}
                        value={steps}
                        onChange={(e) => setSteps(e.target.value)}
                    />
                    <div className="wn-hint">
                        Optional, but this is the part that replaces the phone call.
                    </div>

                    <div className="wn-row">
                        <div>
                            <label className="wn-label" htmlFor={`cat-${item.id}`}>Category</label>
                            <select
                                id={`cat-${item.id}`}
                                className="wn-select"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                {CATEGORY_VALUES.map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="wn-label" htmlFor={`aud-${item.id}`}>Who sees this</label>
                            <select
                                id={`aud-${item.id}`}
                                className="wn-select"
                                value={audience}
                                onChange={(e) => setAudience(e.target.value)}
                            >
                                <option value="selected">Selected organizations</option>
                                <option value="all">All organizations</option>
                            </select>
                        </div>
                    </div>

                    {audience === "all" ? (
                        <div className="wn-hint" style={{ marginTop: 8 }}>
                            Every organization on every selected deployment will see this.
                        </div>
                    ) : (
                        <>
                            <label className="wn-label">Organizations</label>
                            <OrgPicker value={orgs} onChange={setOrgs} />
                        </>
                    )}

                    <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                        <button className="wn-btn wn-btn--danger wn-btn--sm" onClick={remove}>
                            Remove
                        </button>
                        <span style={{ flex: 1 }} />
                        <button
                            className="wn-btn wn-btn--primary"
                            onClick={save}
                            disabled={saving || !dirty}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

const keyOf = (o) => `${o.server_code}:${o.org_id}`;

/* ── confirm + results ───────────────────────────────────────────────────── */

/**
 * Naming the blast radius before the send. "Send to clients" is not an action
 * anybody should take without seeing how far it reaches.
 */
function ConfirmPublish({ release, targetCount, onCancel, onConfirm }) {
    const servers = (release.servers || []).filter((s) => s.version_label);
    return (
        <div className="wn-ov" onClick={(e) => e.target === e.currentTarget && onCancel()}>
            <div className="wn-modal" role="dialog" aria-label="Confirm send">
                <div className="wn-m-head">
                    <h3>Send to clients?</h3>
                    <button className="wn-x" onClick={onCancel} aria-label="Close">×</button>
                </div>
                <div className="wn-m-body">
                    <p style={{ marginTop: 0, fontSize: 14, lineHeight: 1.7 }}>
                        <b>{release.items?.length || 0}</b> update
                        {release.items?.length === 1 ? "" : "s"} will become visible to{" "}
                        <b>{targetCount}</b> on:
                    </p>
                    <div className="wn-result">
                        {servers.map((s) => (
                            <div key={s.id}>
                                <b>{s.server_label || s.server_code}</b>
                                <span>{s.version_label}</span>
                            </div>
                        ))}
                        {servers.length === 0 && (
                            <div><span className="wn-msg">No deployment has a version number set.</span></div>
                        )}
                    </div>
                    <div className="wn-hint" style={{ marginTop: 12 }}>
                        Customers see this the next time they open What&apos;s New in
                        eMedHub. You can retract it afterwards, but anyone who has already
                        read it will have seen it.
                    </div>
                </div>
                <div className="wn-m-foot">
                    <button className="wn-btn" onClick={onCancel}>Cancel</button>
                    <button
                        className="wn-btn wn-btn--primary"
                        onClick={onConfirm}
                        disabled={servers.length === 0}
                    >
                        Send now
                    </button>
                </div>
            </div>
        </div>
    );
}

function PublishResults({ results, onClose }) {
    const failed = results.filter((r) => !r.ok);
    return (
        <div className="wn-ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="wn-modal" role="dialog" aria-label="Send results">
                <div className="wn-m-head">
                    <h3>{failed.length ? "Sent with problems" : "Sent"}</h3>
                    <button className="wn-x" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="wn-m-body">
                    <div className="wn-result">
                        {results.map((r) => (
                            <div key={r.server}>
                                <b>{r.server}</b>
                                <span className={r.ok ? "wn-chip wn-chip--published" : "wn-chip wn-chip--failed"}>
                                    {r.ok ? "Sent" : "Failed"}
                                </span>
                                {!r.ok && <span className="wn-msg">{r.error}</span>}
                            </div>
                        ))}
                    </div>
                    {failed.length > 0 && (
                        <div className="wn-hint" style={{ marginTop: 12 }}>
                            The deployments that succeeded really did receive it. Fix the
                            problem above and press Re-send — it is safe to repeat, and
                            only the failed deployments change.
                        </div>
                    )}
                </div>
                <div className="wn-m-foot">
                    <button className="wn-btn wn-btn--primary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}
